/** `plan()`: inputs + drone + optional DTM → the plan the backend stores (spec §4). */
import { footprint, spacings } from './camera';
import { frameAround, rotate, toLocal, toWgs, type Frame } from './frame';
import { deriveSpeed, gridLines, orderLines, TURN_PENALTY_S, type GridLine } from './grid';
import { area, autoAngle, bounds, buffer, ring } from './polygon';
import { buildProfile, clearance, pathLength, round } from './stats';
import { heightAt, simplifyIndices } from './terrain';
import type { Dtm, DroneSpec, LngLat, MissionInputs, Plan, PlanWarning, Waypoint, WaypointAction, XY } from './types';

export const MANY_WAYPOINTS = 300;
const CLEARANCE_LIMIT_M = 120;
export const LOW_CLEARANCE_M = 30;
// The backend's caps on a saved plan (backend/missions/schemas.py).
const MAX_LINES = 2000;
const MAX_PHOTOS = 20000;
const MAX_WAYPOINTS = 2000;

/** Why `plan()` throws; the message of the thrown Error. */
export type PlanError = 'polygon' | 'spacing' | 'too_large';

function fail(reason: PlanError): never {
  throw new Error(reason);
}

/** Flight-line compass bearing (0 = north–south) → math angle (counter-clockwise from +x). */
export function bearingToMath(bearing: number): number {
  return ((90 - bearing) % 180 + 180) % 180;
}
export function mathToBearing(deg: number): number {
  return ((90 - deg) % 180 + 180) % 180;
}
/** The stored bearing: rounded to 3 decimals and kept in [0, 180), which rounding alone can leave. */
export function bearingOut(math: number): number {
  return round(mathToBearing(math), 3) % 180;
}

interface Pt {
  xy: XY; // local frame
  ll: LngLat;
}

export function plan(inputs: MissionInputs, drone: DroneSpec, dtm: Dtm | null): Plan {
  if (inputs.polygon.length < 3) fail('polygon');
  const frame: Frame = frameAround(inputs.polygon);
  const polyLocal = ring(inputs.polygon.map((p) => toLocal(frame, p)));

  const fp = footprint(drone.camera, inputs.altitude_m);
  const { line_m, photo_m } = spacings(fp, inputs.forward_overlap, inputs.side_overlap);
  if (!(line_m > 0) || !(photo_m > 0)) fail('spacing');

  const math = inputs.angle_deg === null ? autoAngle(polyLocal) : bearingToMath(inputs.angle_deg);
  const toRot = (p: XY) => rotate(p, -math);
  const fromRot = (p: XY) => rotate(p, math);
  // Re-oriented counter-clockwise defensively; a rotation alone keeps the orientation.
  const buffered = ring(buffer(polyLocal.map(toRot), inputs.margin_m));
  const b = bounds(buffered);
  const estLines = Math.floor((b.maxY - b.minY) / line_m + 1e-9) + 2;
  const estPhotos = estLines * (Math.floor((b.maxX - b.minX) / photo_m + 1e-9) + 2);
  if (estLines > MAX_LINES || estPhotos > MAX_PHOTOS || (inputs.trigger === 'waypoint' && estPhotos > MAX_WAYPOINTS)) fail('too_large');
  const takeoffLocal = inputs.takeoff ? toLocal(frame, inputs.takeoff) : null;
  const lines: GridLine[] = orderLines(gridLines(buffered, line_m, photo_m), takeoffLocal ? toRot(takeoffLocal) : null);
  const { speed_ms, capped } = deriveSpeed(inputs, photo_m, drone.max_speed_ms);

  const takeoffElev = dtm && inputs.takeoff ? dtm.sample(inputs.takeoff[0], inputs.takeoff[1]) : null;
  const dtmUnavailable = inputs.terrain_follow && takeoffElev === null;
  const follow = inputs.terrain_follow && !dtmUnavailable;
  const hover: WaypointAction[] = inputs.hover_s > 0 ? ['hover'] : [];

  const pt = (xRot: number, yRot: number): Pt => {
    const xy = fromRot({ x: xRot, y: yRot });
    return { xy, ll: toWgs(frame, xy) };
  };
  const height = (p: Pt) => heightAt(follow ? dtm : null, takeoffElev, p.ll, inputs.altitude_m);

  const photos: Pt[] = [];
  const lineEnds: [LngLat, LngLat][] = [];
  const wps: { p: Pt; h: number; actions: WaypointAction[] }[] = [];
  const photoGapExtra: number[] = []; // turn penalty before photo i (0 for same line)

  lines.forEach((line, li) => {
    const pts = line.xs.map((x) => pt(x, line.y));
    lineEnds.push([pts[0].ll, pts[pts.length - 1].ll]);
    pts.forEach((p, j) => {
      photos.push(p);
      photoGapExtra.push(li > 0 && j === 0 ? TURN_PENALTY_S : 0);
    });
    if (inputs.trigger === 'waypoint') {
      pts.forEach((p) => wps.push({ p, h: height(p), actions: ['takePhoto', ...hover] }));
      return;
    }
    const hs = pts.map(height);
    const kept = follow ? simplifyIndices(line.xs, hs, inputs.terrain_tolerance_m) : [0, pts.length - 1];
    kept.forEach((i) => wps.push({ p: pts[i], h: hs[i], actions: [...hover] }));
  });
  if (inputs.trigger === 'interval' && wps.length) wps[0].actions = ['takePhoto', ...hover];

  const waypoints: Waypoint[] = wps.map((w) => ({ lng: round(w.p.ll[0], 8), lat: round(w.p.ll[1], 8), height_m: round(w.h, 1), actions: w.actions }));

  const pathPts = [...(takeoffLocal ? [takeoffLocal] : []), ...wps.map((w) => w.p.xy), ...(takeoffLocal ? [takeoffLocal] : [])];
  const path_m = pathLength(pathPts);
  const flight_s = path_m / speed_ms + inputs.hover_s * wps.length + TURN_PENALTY_S * (lines.length - 1);

  // Without a takeoff elevation flight heights are relative, so terrain is left out of the profile.
  const profile = buildProfile(
    wps.map((w) => w.p.xy),
    wps.map((w) => w.h),
    frame,
    takeoffElev === null ? null : dtm,
    takeoffElev,
  );
  const clr = clearance(profile);

  const photo_gaps_s: number[] = [];
  for (let i = 1; i < photos.length; i++) {
    const d = Math.hypot(photos[i].xy.x - photos[i - 1].xy.x, photos[i].xy.y - photos[i - 1].xy.y);
    photo_gaps_s.push(round(d / speed_ms + photoGapExtra[i] + (inputs.trigger === 'waypoint' ? inputs.hover_s : 0), 2));
  }

  const warnings: PlanWarning[] = [];
  if (capped) warnings.push('speed_capped');
  if (clr.max !== null && clr.max > CLEARANCE_LIMIT_M) warnings.push('clearance_above_120');
  if (waypoints.some((w) => w.height_m < 0)) warnings.push('negative_height');
  if (waypoints.length > MANY_WAYPOINTS) warnings.push('many_waypoints');
  if (dtmUnavailable) warnings.push('dtm_unavailable');
  if (clr.min !== null && clr.min < LOW_CLEARANCE_M) warnings.push('low_clearance');

  return {
    version: 1,
    angle_deg: bearingOut(math),
    speed_ms: round(speed_ms, 2),
    lines: lineEnds.map(([a, b]) => [[round(a[0], 8), round(a[1], 8)], [round(b[0], 8), round(b[1], 8)]]),
    photos: photos.map((p) => [round(p.ll[0], 8), round(p.ll[1], 8)]),
    waypoints,
    profile,
    photo_gaps_s,
    stats: {
      area_ha: round(area(polyLocal) / 10000, 2),
      gsd_cm: round(fp.gsd_cm, 2),
      lines: lines.length,
      photos: photos.length,
      path_m: round(path_m, 0),
      flight_s: round(flight_s, 0),
      line_spacing_m: round(line_m, 2),
      photo_spacing_m: round(photo_m, 2),
      clearance_min_m: clr.min,
      clearance_max_m: clr.max,
    },
    warnings,
  };
}
